import PropTypes from 'prop-types';
import React, { Fragment, useContext, useMemo } from 'react';

import {
  UK_COUNTRY_CODE,
  IRELAND_COUNTRY_CODE,
  CANADA_COUNTRY_CODE
} from '../../../../constants';

import { SignUpContext } from '../../SignUp';

import InputField from '../../../widgets/InputField';
import DropdownField from '../../../widgets/DropdownField';
import PhoneNumberField from '../../../widgets/PhoneNumberField';

import styles from '../../SignUp.module.scss';

const SignUpFormStep2 = ({
  fields,
}) => {
  const {
    countryId,
    countries,
    provinces,
    onPropertyChange,
    onBatchPropertyUpdate,
    onInputBlur,
  } = useContext(SignUpContext);

  const {
    country,
    province = {},
    'address-line-1': addressLine1,
    city,
    'post-code': postCode,
    'phone-number': phoneNumber,
  } = fields;

  const isCanadaUser = countryId === CANADA_COUNTRY_CODE;

  const { metadata: { prefix: phonePrefix, abslength } } = phoneNumber;

  const onCountryChange = ({ name, value = {} }) => {
    const { name: countryName } = value || {};

    const newProvince = { name: 'province', value: countryName === 'Canada' ? provinces[0].id : '' };

    onBatchPropertyUpdate({
      country: { name, value: countryName },
      province: newProvince,
      'post-code': { name: 'city', value: '' },
      'address-line-1': { name: 'address-line-1', value: '' },
      'address-line-2': { name: 'address-line-2', value: '' },
      city: { name: 'city', value: '' },
      'phone-number': { name: 'phone-number', value: '' },
    });
  };

  const onProvinceChange = ({ name, value = {} }) => {
    const { name: provinceName } = value || {};

    onBatchPropertyUpdate({
      province: { name, value: provinceName },
      'post-code': { name: 'city', value: '' },
      'address-line-1': { name: 'address-line-1', value: '' },
      'address-line-2': { name: 'address-line-2', value: '' },
      city: { name: 'city', value: '' },
      'phone-number': { name: 'phone-number', value: '' },
    });
  };

  const editedCountryList = useMemo(() => {
    const ireland = countries.find(({ 'country-id': id }) => id === IRELAND_COUNTRY_CODE);
    const uk = countries.find(({ 'country-id': id }) => id === UK_COUNTRY_CODE);

    let processedCountries = countries;

    if (ireland) {
      processedCountries = processedCountries
        .filter(({ 'country-id': id }) => id !==  IRELAND_COUNTRY_CODE)
        .unshift(ireland);
    }

    if (uk) {
      processedCountries = processedCountries
        .filter(({ 'country-id': id }) => id !==  UK_COUNTRY_CODE)
        .unshift(uk);
    }
    return processedCountries.toJS();
  }, [countries, IRELAND_COUNTRY_CODE, UK_COUNTRY_CODE]);

  return (
    <Fragment>
      <DropdownField
        inline
        fullWidth
        label={country.label}
        name={country.name}
        dataHook={country.dataHook}
        theme="dark"
        value={country.value}
        items={editedCountryList}
        className={styles.dropdownField}
        onPropertyChange={onCountryChange}
        error={country.error}
      />
      {isCanadaUser ?
        <div>
          <DropdownField
            inline
            fullWidth
            label={province.label}
            name={province.name}
            dataHook={province.dataHook}
            theme="dark"
            value={province.value}
            items={provinces}
            className={styles['dropdownField--provinces']}
            onPropertyChange={onProvinceChange}
            error={province.error}
          />
          <span className={styles.noAvailableProvinceMsg}>Matchbook is not available in Ontario</span>
        </div>
        : null
      }
      <InputField
        label={addressLine1.label}
        name={addressLine1.name}
        dataHook={addressLine1.dataHook}
        type="text"
        value={addressLine1.value}
        onPropertyChange={onPropertyChange}
        className={styles.inputField}
        onInputBlur={onInputBlur}
        error={addressLine1.error}
        inline
      />
      <InputField
        label={city.label}
        name={city.name}
        dataHook={city.dataHook}
        type="text"
        value={city.value}
        onPropertyChange={onPropertyChange}
        className={styles.inputField}
        onInputBlur={onInputBlur}
        error={city.error}
        inline
      />
      <div style={{ paddingRight: 72 }}>
        <InputField
          label={postCode.label}
          name={postCode.name}
          dataHook={postCode.dataHook}
          type="text"
          value={postCode.value}
          onPropertyChange={onPropertyChange}
          className={styles.inputField}
          onInputBlur={onInputBlur}
          error={postCode.error}
          size="small"
          inline
        />
      </div>
      <PhoneNumberField
        fullWidth
        countryId={countryId}
        label={phoneNumber.label}
        name={phoneNumber.name}
        dataHook={phoneNumber.dataHook}
        value={phoneNumber.value}
        onPropertyChange={onPropertyChange}
        className={styles.inputField}
        onInputBlur={onInputBlur}
        error={phoneNumber.error}
        prefix={phonePrefix}
        abslength={abslength}
        theme="dark"
      />
    </Fragment>
  );
};

SignUpFormStep2.propTypes = {
  fields: PropTypes.instanceOf(Object).isRequired,
};

export default SignUpFormStep2;
